import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';
import { useState } from 'react';
import Maybe from 'true-myth/maybe';

import { useTranslate } from '~/shared/context/use-translate';
import { contains } from '~/shared/data-contract/filter';
import type { User } from '~/shared/data-contract/user';
import { Badge } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import { Dialog } from '~/ui/core/dialog/Dialog';
import { IconCrossSmall, IconPeopleAdd } from '~/ui/core/icons';
import { Input } from '~/ui/core/input/Input';

import { getUserName } from '../../app/common/user';
import { useSnackbars } from '../../app/error-handling/SnackBars';
import { useUsersFilterQuery } from '../../app/query-hooks/users/users';
import { useAddFeaturedLearningOpportunityMutation } from '../api';

export const FeatureLearningOpportunityDialog: FunctionComponent<{
    readonly learningOpportunityId: string;
    readonly trigger?: ReactNode;
}> = ({ learningOpportunityId, trigger }) => {
    const translate = useTranslate();
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [employees, setEmployees] = useState<readonly User[]>([]);
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbars();
    const { mutate } = useAddFeaturedLearningOpportunityMutation(learningOpportunityId);
    const filter = is.nonEmptyString(inputValue)
        ? {
              filters: [
                  contains<Record<string, string>, string>('fullName', inputValue, true),
                  contains<Record<string, string>, string>('fullNameReversed', inputValue, true),
              ],
              op: 'or' as const,
          }
        : undefined;
    const { data } = useUsersFilterQuery({ filter }, inputValue.length >= 2);
    const selectedIds = new Set(employees.map((employee) => employee.id));
    const options = data?.data.filter((employee: User) => !selectedIds.has(employee.id)) ?? [];

    const save = (): void => {
        mutate(
            employees.map((employee) => employee.id),
            {
                onError: showErrorSnackbar,
                onSuccess: () => showSuccessSnackbar('success.feature-opportunities.feature'),
            },
        );
        setEmployees([]);
        setOpen(false);
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <Dialog.Trigger asChild={true}>
                {trigger ?? (
                    <Button iconPosition="leading" size="sm" variant="secondary">
                        <IconPeopleAdd size={14} />
                        {translate('dialogs.learning-opportunities.title')}
                    </Button>
                )}
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content className="max-w-[560px]">
                    <Dialog.Title>{translate('dialogs.learning-opportunities.title')}</Dialog.Title>
                    <Dialog.Description>{translate('dialogs.learning-opportunities.text')}</Dialog.Description>
                    <div className="flex flex-col gap-m">
                        <Input
                            placeholder={translate('dialogs.learning-opportunities.search-placeholder')}
                            value={inputValue}
                            onChange={(event) => setInputValue(event.target.value)}
                        />
                        {options.length > 0 && (
                            <div className="flex max-h-[180px] flex-col overflow-auto rounded-md border border-gray-300 bg-white-200">
                                {options.map((employee: User) => (
                                    <button
                                        key={employee.id}
                                        className="cursor-pointer border-0 bg-transparent px-m py-s text-left hover:bg-gray-100"
                                        type="button"
                                        onClick={() => setEmployees([...employees, employee])}
                                    >
                                        {getUserName(Maybe.of(employee))}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="flex flex-wrap gap-s">
                            {employees.map((employee) => (
                                <Badge
                                    key={employee.id}
                                    variant="gray"
                                    onClick={() => setEmployees(employees.filter((item) => item.id !== employee.id))}
                                >
                                    {getUserName(Maybe.of(employee))}
                                    <IconCrossSmall size={12} />
                                </Badge>
                            ))}
                        </div>
                        <div className="flex justify-end gap-s">
                            <Button variant="secondary" onClick={() => setOpen(false)}>
                                {translate('dialogs.buttons.cancel')}
                            </Button>
                            <Button disabled={employees.length === 0} onClick={save}>
                                {translate('dialogs.buttons.save')}
                            </Button>
                        </div>
                    </div>
                    <Dialog.Close aria-label="close">
                        <IconCrossSmall />
                    </Dialog.Close>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog>
    );
};
